import { PaymentMethodFragment, useStripeSetupIntentSecretQuery } from '@alice-financial/alice-sdk'
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@alice-financial/pretext-ui'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Elements } from '@stripe/react-stripe-js'
import * as React from 'react'
import {
  ConfirmACHConnection,
  ConfirmStripeACHConnection,
} from '../../../externalServices/stripe/ConfirmACHConnection'
import { ConnectBankAccountForm } from '../../../externalServices/stripe/ConnectBankAccountForm'
import { useIssuingStripe } from '../../../externalServices/stripe/useLoadStripe'
import { ConnectACHPaymentSourceForm, ConnectStripeACHForm } from '../../banking/ConnectACHForm'
import { ACHNumbers } from '../../banking/types'
import { useCreateACHPaymentSource } from '../../banking/useConnectACHForm'
import { useOrgId } from '../../useOrgId'
import { useIssuingClientSecret } from '../useIssuingClientSecret'
import { TypedBankableEntity } from './types'
import { useCreateAliceCardPaymentMethod } from './useCreateAliceCardPaymentMethod'

type ConnectPaymentProps = {
  clientSecret: string
  currentPaymentMethod: PaymentMethodFragment | undefined
  onError: () => void
}

type ConnectACHProps = Omit<ConnectPaymentProps, 'currentPaymentMethod'> & {
  onSuccess: (achNumbers: ACHNumbers) => void
}
type StripeCompanyAccountId = number // Tilda pk id from our database
type StripeConnectOptionProps = ConnectPaymentProps & {
  onSuccess: () => void
  stripeCompanyAccountId: StripeCompanyAccountId | undefined
}

const BankingACHConnectionInstructions = () => (
  <>
    <Typography variant="body2" component="p" gutterBottom>
      Add the account where employee payroll is deducted. Each pay period, employees&apos; pretax
      contributions will transfer to your organization&apos;s Contribution Account.
    </Typography>
    <Typography variant="body2" component="p" gutterBottom>
      Please note these are not payments to Alice — these transfers are employee pretax benefit contributions.
    </Typography>
  </>
)

const ACHOption = ({ clientSecret, onSuccess, onError }: ConnectACHProps) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
      <Typography variant="body2" color="primary" fontWeight="bold">
        Provide account &amp; routing numbers
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <ConnectStripeACHForm clientSecret={clientSecret} onSuccess={onSuccess} onError={onError}>
        <BankingACHConnectionInstructions />
      </ConnectStripeACHForm>
    </AccordionDetails>
  </Accordion>
)

type SecretACHMandateReacceptanceProps = {
  currentPaymentMethod: PaymentMethodFragment
  stripeCompanyAccountId: StripeCompanyAccountId
}
const SecretACHMandateReacceptance = ({
  currentPaymentMethod,
  stripeCompanyAccountId,
}: SecretACHMandateReacceptanceProps) => {
  const [showMandate, setShowMandate] = React.useState(false)
  const { data: clientSecret } = useIssuingClientSecret(
    stripeCompanyAccountId,
    currentPaymentMethod.stripeSystemId
  )

  if (showMandate && !clientSecret) return null

  return (
    <>
      <Typography color="transparent" onClick={() => setShowMandate(true)} variant="caption">
        π
      </Typography>
      {showMandate && clientSecret && (
        <ConfirmStripeACHConnection
          type="banking"
          clientSecret={clientSecret}
          handleConfirmationResponse={() => setShowMandate(false)}
          invalidationKeys={[
            useIssuingClientSecret.getKey(stripeCompanyAccountId, currentPaymentMethod.stripeSystemId),
          ]}
        />
      )}
    </>
  )
}

// Login to your bank via Stripe to verify
const StripeConnectOption = ({
  currentPaymentMethod,
  clientSecret,
  onSuccess,
  stripeCompanyAccountId,
}: StripeConnectOptionProps) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
      <Typography variant="body2" color="primary" fontWeight="bold">
        Log in to verify
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <ConnectBankAccountForm clientSecret={clientSecret} onSuccess={onSuccess} />
      {currentPaymentMethod && currentPaymentMethod.status !== 'inactive' && stripeCompanyAccountId && (
        <SecretACHMandateReacceptance
          currentPaymentMethod={currentPaymentMethod}
          stripeCompanyAccountId={stripeCompanyAccountId}
        />
      )}
    </AccordionDetails>
  </Accordion>
)

type AddNewPaymentMethodProps = {
  onSuccess?: () => void
  entity: TypedBankableEntity
  currentPaymentMethod: PaymentMethodFragment | undefined
  willBeFirstPaymentMethod: boolean
}
/**
 * When a new payment method needs to be connected to fund Alice Card, this component allows
 * the admin to choose between:
 * 1. Directly connect ACH using account and routing numbers
 * 2. Start the Stripe flow to connect a bank account
 *
 * Both options then require the user to accept the ACH mandate
 */
export const AddNewStripePaymentMethod = ({
  currentPaymentMethod,
  onSuccess,
  entity,
  willBeFirstPaymentMethod,
}: AddNewPaymentMethodProps) => {
  const orgId = useOrgId()
  const [showACHMandate, setShowACHMandate] = React.useState(false)
  const { data: stripeSetupIntentSecretData } = useStripeSetupIntentSecretQuery({ orgId: useOrgId() })
  const companyAccount = stripeSetupIntentSecretData?.organization?.aliceCardProgram.companyAccount
  const [routingNumber, setRoutingNumber] = React.useState<string | null>(null)
  const [accountNumber, setAccountNumber] = React.useState<string | null>(null)
  const { mutateAsync: createPaymentMethod } = useCreateAliceCardPaymentMethod(
    entity,
    { accountNumber, routingNumber },
    { onSuccess }
  )

  const pmId = currentPaymentMethod?.status !== 'inactive' ? currentPaymentMethod?.stripeSystemId : null
  const { data: issuingSecret, refetch } = useIssuingClientSecret(companyAccount?.id, pmId)
  // id of connected account in stripe, e.g. acct_abc123
  const stripeAccountSystemId = companyAccount?.stripeAccountId
  const stripePromise = useIssuingStripe({ stripeAccount: stripeAccountSystemId })

  if (!stripeAccountSystemId || !issuingSecret) return null

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: issuingSecret }}>
      {showACHMandate ? (
        <Accordion expanded={true}>
          <AccordionDetails>
            <ConfirmStripeACHConnection
              type="banking"
              clientSecret={issuingSecret}
              handleConfirmationResponse={createPaymentMethod}
              invalidationKeys={[useStripeSetupIntentSecretQuery.getKey({ orgId })]}
              willBeFirstPaymentMethod={willBeFirstPaymentMethod}
            />
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <Typography variant="body2" gutterBottom>
            Choose how you would like to verify the bank account that you use to withdraw payroll for{' '}
            <strong>{entity.name}</strong>
          </Typography>
          <ACHOption
            clientSecret={issuingSecret}
            onSuccess={(achNumbers: ACHNumbers) => {
              setRoutingNumber(achNumbers.routingNumber)
              setAccountNumber(achNumbers.accountNumber)
              setShowACHMandate(true)
            }}
            onError={() => refetch()}
          />
          <StripeConnectOption
            currentPaymentMethod={currentPaymentMethod}
            clientSecret={issuingSecret}
            stripeCompanyAccountId={companyAccount?.id}
            onSuccess={() => setShowACHMandate(true)}
            onError={() => refetch()}
          />
        </>
      )}
    </Elements>
  )
}

export const AddNewACHPaymentSource = ({
  entity,
  onSuccess,
  willBeFirstPaymentMethod,
}: AddNewPaymentMethodProps) => {
  const [showACHMandate, setShowACHMandate] = React.useState(false)
  const [achNumbers, setAchNumbers] = React.useState<ACHNumbers | null>(null)

  const { mutate: createACHPaymentSource } = useCreateACHPaymentSource(entity, { onSuccess })
  if (achNumbers && showACHMandate) {
    return (
      <ConfirmACHConnection
        onAccept={() => createACHPaymentSource(achNumbers)}
        type="banking"
        willBeFirstPaymentMethod={willBeFirstPaymentMethod}
      />
    )
  }
  return (
    <ConnectACHPaymentSourceForm
      onSuccess={(values) => {
        setAchNumbers(values)
        setShowACHMandate(true)
      }}
    >
      <BankingACHConnectionInstructions />
    </ConnectACHPaymentSourceForm>
  )
}
